import type {
  AppleAuthPayload,
  AuthUserDto,
  AuthValidateOtpPayload,
  OtpMailData,
  RegisterFormData,
  UserFormData,
} from '@/core/data/auth/auth.interface';
import type { AuthRepository, EnhancedAuthUserDto } from '@/core/data/auth/auth.repository';
import type { LoginMethodType } from '@/core/data/auth/auth.type';
import type { CodeResponse } from '@/core/data/auth/google/oauth2';
import { signInMethodGroups } from '@/core/data/auth/loginMethods';
import type { AuthMethodItem } from '@/core/data/config/uiConfig.interface';
import type { UserRatings } from '@/core/data/rating/rating.interface';
import type { ConfigService } from '@/core/domain/config.service';
import { clearHandoffCookie, readHandoffCookie } from '@/core/helpers/handoff-cookie.helper';
import type { ApiError, Result } from '@/core/network/http/httpClient.interface';
import { generalError } from '@/core/network/http/httpError';
import { useAuthStore } from '@/stores/auth.store';
import { useNotificationStore } from '@/stores/notification.store';
import type { AuthScreenMethodGroup } from '@/views/composables/auth/useAuth.interface';
import type { BaseUserForm } from '@/views/composables/models/form.interface';

export class AuthService {
  private readonly repository: AuthRepository;
  private readonly configService: ConfigService;
  private profileImage: string | null = null;
  private chessboardImage: string | null = null;

  constructor(repository: AuthRepository, configService: ConfigService) {
    this.repository = repository;
    this.configService = configService;
  }

  async authWithEmailUsername(value: string): Promise<Result<boolean | null>> {
    try {
      const response = await this.repository.authWithEmailUsername(value);
      return response ? { data: response } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async authWithGoogle(code: CodeResponse): Promise<Result<AuthUserDto>> {
    try {
      const response = await this.repository.authGoogle(code);
      return response ? { data: response } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async authWithApple(payload: AppleAuthPayload): Promise<Result<AuthUserDto>> {
    try {
      const response = await this.repository.authApple(payload);
      return response ? { data: response } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async ensureHandoffCookie(): Promise<void> {
    await this.repository.ensureHandoffCookie();
  }

  async getToken(code: string): Promise<Result<AuthUserDto>> {
    try {
      const response = await this.repository.getToken(code);
      return response ? { data: response } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async validateOtp(payload: AuthValidateOtpPayload): Promise<Result<EnhancedAuthUserDto>> {
    try {
      const response = await this.repository.authValidateOtp(payload);
      return response ? { data: response } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  async submitPersonalInfo(form: BaseUserForm): Promise<Result<AuthUserDto | null>> {
    try {
      if (!form.birthDate) {
        return { error: { code: 400, message: 'Missing birth date.' } };
      }

      const registerData: UserFormData = {
        firstName: form.firstName,
        lastName: form.lastName,
        username: form.username,
        emoji: form.emoji,
        birthDate: new Date(form.birthDate).toISOString(),
      };

      const response = await this.repository.submitPersonalInfo(registerData);

      if (response) {
        return { data: response };
      } else {
        return { error: generalError() };
      }
    } catch (error) {
      // gambits.authService.logout();

      return {
        error: error as ApiError,
      };
    }
  }

  async submitRatings(ratings: UserRatings): Promise<Result<AuthUserDto | null>> {
    try {
      const response = await this.repository.submitRatings(ratings);
      if (response) {
        this.clearImages();
        return { data: response };
      } else {
        return { error: generalError() };
      }
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  private clearImages(): void {
    this.profileImage = null;
    this.chessboardImage = null;
  }

  async delete(): Promise<Result<boolean>> {
    try {
      const response = await this.repository.deleteUser();
      return response ? { data: response } : { error: generalError() };
    } catch (error) {
      return {
        error: error as ApiError,
      };
    }
  }

  registerUserFormData(formData: BaseUserForm): void {
    this.repository.saveTempRegisterData({
      firstName: formData.firstName,
      lastName: formData.lastName,
      username: formData.username,
      birthDate: formData.birthDate ? new Date(formData.birthDate).toISOString() : '',
      emoji: formData.emoji || undefined,
    });

    // Images are (can be) too big to be put into local storage so we store them here.
    // It would be nice to find a better solution in the future.
    // Maybe we could upload them to the server and store the blobhash in the local storage.
    this.profileImage = formData.profileImage ?? null;
    this.chessboardImage = formData.chessboardImage ?? null;
  }

  getFormData(): RegisterFormData | null {
    return this.repository.getRegisterData();
  }

  clearFormData() {
    return this.repository.removeOnboardingData();
  }

  getUser(): AuthUserDto | null {
    return this.repository.getUser();
  }

  isAuthenticated(): boolean {
    return this.repository.isAuthenticated();
  }

  isActive(): boolean {
    return !!this.getUser()?.isActive;
  }

  isAdmin(): boolean {
    return !!this.getUser()?.isAdmin;
  }

  getOtpMailData(): OtpMailData | null {
    return this.repository.getOtpMailData();
  }

  getOtpSendDelay(): number {
    const otpMailData = this.repository.getOtpMailData();
    if (otpMailData?.sendTime) {
      const otpDate = new Date(otpMailData.sendTime);
      otpDate.setSeconds(otpDate.getSeconds() + this.configService.getOtpDelay());

      // Calculate the difference in seconds
      const differenceInSeconds = Math.round((otpDate.getTime() - new Date().getTime()) / 1000);
      return differenceInSeconds > 0 ? differenceInSeconds : 0;
    }
    return 0;
  }

  private configureManifest(
    manifestConfiguration: AuthMethodItem[],
    loginMethodGroup: AuthScreenMethodGroup,
  ): AuthScreenMethodGroup {
    return {
      ...loginMethodGroup,
      items: loginMethodGroup.items
        ?.map((loginMethod) => {
          const manifestConfig = manifestConfiguration.find((el) => el.type === loginMethod.type);
          return {
            ...loginMethod,
            active: manifestConfig?.active || false,
            position: manifestConfig?.position || loginMethod.position,
            clientId: manifestConfig?.clientId,
          };
        })
        .sort((a, b) => (a.position > b.position ? 1 : -1)),
    };
  }

  getAuthMethods(): AuthScreenMethodGroup[] {
    const configMethods = this.configService.getAuthMethods();

    return signInMethodGroups
      .map((signInMethodGroup) => this.configureManifest(configMethods, signInMethodGroup))
      .sort((a, b) => (a.position > b.position ? 1 : -1));
  }

  saveConnecting(type: LoginMethodType): void {
    this.repository.setConnecting(type);
  }

  getConnecting(): LoginMethodType | null {
    return this.repository.getConnecting();
  }

  removeConnecting(): void {
    this.repository.removeConnecting();
  }

  async logout(): Promise<void> {
    await useNotificationStore().clear();
    // Ahead of the repository, which drops the JWT the revoke call authenticates
    // with. Clearing the cookie alone would leave the code redeemable by a later
    // install until it expired.
    await this.discardHandoff();
    await useAuthStore().logoutUser();
    await this.repository.logout();
  }

  private async discardHandoff(): Promise<void> {
    const parked = readHandoffCookie();
    clearHandoffCookie();

    if (parked) {
      await this.repository.revokeHandoffCode(parked.code);
    }
  }
}
